import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "../../api/axiosInstance";
import toast, { Toaster } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes } from "react-icons/fa";

/* ─── CSS ─────────────────────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,600&family=Inter:wght@400;500;600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}

.jblf-page {
  width: 100vw;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1A0505 0%, #3D0A0A 45%, #5A1010 100%);
  padding: clamp(16px, 4vw, 32px);
  position: relative;
  overflow: hidden;
  font-family: 'Inter', sans-serif;
}

/* dot-grid watermark */
.jblf-dust {
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background-image: radial-gradient(circle, rgba(201,150,42,0.10) 1px, transparent 1px);
  background-size: 36px 36px;
}

/* rotating mandala — background */
.jblf-mandala {
  position: fixed;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: min(90vw, 700px); height: min(90vw, 700px);
  opacity: 0.05; pointer-events: none; z-index: 0;
  animation: jblfSpin 90s linear infinite;
}
@keyframes jblfSpin { to { transform: translate(-50%, -50%) rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .jblf-mandala { animation: none; } }

/* ── Two-col layout ── */
.jblf-wrap {
  position: relative; z-index: 1;
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 860px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 32px 80px rgba(0,0,0,0.6);
  border: 1px solid rgba(201,150,42,0.25);
}

/* gold top bar */
.jblf-wrap::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, transparent, #C9962A 30%, #E8C46A 50%, #C9962A 70%, transparent);
  z-index: 3;
}

/* ── LEFT PANEL ── */
.jblf-left {
  flex: 0 0 42%;
  background: linear-gradient(160deg, #7B1C1C 0%, #3D0A0A 100%);
  padding: clamp(32px,5vw,52px) clamp(24px,4vw,40px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: clamp(14px,2vw,20px);
  position: relative;
  overflow: hidden;
}

.jblf-om {
  font-size: clamp(36px,5.5vw,52px);
  color: #C9962A; font-family: serif; line-height: 1;
  filter: drop-shadow(0 0 12px rgba(201,150,42,0.35));
}

.jblf-tagline {
  font-family: 'Playfair Display', serif;
  font-size: clamp(20px,2.8vw,30px);
  font-weight: 700; color: #FDF6EC; line-height: 1.35;
}
.jblf-tagline em { font-style: italic; color: #E8C46A; }

.jblf-divrow {
  display: flex; align-items: center; gap: 10px; width: 100%;
}
.jblf-divline { flex: 1; height: 1px; background: rgba(201,150,42,0.35); }
.jblf-divsym  { color: #C9962A; font-size: 15px; }

.jblf-sub {
  font-size: clamp(12px,1.3vw,14px);
  color: rgba(253,246,236,0.72);
  line-height: 1.7; max-width: 260px;
}

.jblf-pills { list-style: none; display: flex; flex-direction: column; gap: 9px; }
.jblf-pill  { display: flex; align-items: center; gap: 9px;
  font-size: clamp(11px,1.2vw,12.5px); color: rgba(253,246,236,0.82); }
.jblf-dot   { width: 5px; height: 5px; border-radius: 50%; background: #C9962A; flex-shrink: 0; }

/* ── RIGHT PANEL (FORM) ── */
.jblf-right {
  flex: 1;
  background: #FDF6EC;
  padding: clamp(28px,5vw,52px) clamp(24px,4vw,44px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: clamp(14px,2vw,20px);
}

.jblf-form-head { text-align: center; }
.jblf-form-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(20px,2.5vw,26px);
  font-weight: 700; color: #7B1C1C; margin-bottom: 5px;
}
.jblf-form-title em { font-style: italic; color: #A87820; }
.jblf-form-sub { font-size: clamp(11.5px,1.2vw,13px); color: #8B7355; }

/* lotus divider */
.jblf-lrow { display: flex; align-items: center; gap: 10px; }
.jblf-lline { flex: 1; height: 1px; background: rgba(201,150,42,0.35); }

/* error banner */
.jblf-error {
  background: rgba(192,57,43,0.1);
  border: 1px solid rgba(192,57,43,0.4);
  border-radius: 8px;
  padding: 10px 14px;
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; color: #C0392B; font-weight: 500;
}
.jblf-error svg { flex-shrink: 0; }

/* fields */
.jblf-field { display: flex; flex-direction: column; gap: 5px; }
.jblf-label {
  font-size: 11px; font-weight: 600; color: #5A1010;
  letter-spacing: 0.5px; text-transform: uppercase;
}
.jblf-input {
  padding: clamp(9px,1.3vw,11px) 14px;
  border: 1.5px solid #D9C8A8;
  border-radius: 8px;
  font-size: clamp(13px,1.3vw,14px);
  color: #2C1A0E; background: #fff;
  font-family: 'Inter', sans-serif; outline: none;
  transition: border-color 0.18s, box-shadow 0.18s;
  -webkit-appearance: none;
}
.jblf-input:focus {
  border-color: #C9962A;
  box-shadow: 0 0 0 3px rgba(201,150,42,0.12);
}
.jblf-input.err { border-color: #C0392B; }

/* forgot */
.jblf-forgot {
  text-align: right; margin-top: -8px;
}
.jblf-forgot a {
  font-size: 11.5px; color: #7B1C1C;
  text-decoration: underline; font-weight: 500;
}
.jblf-forgot a:hover { color: #A87820; }

/* submit */
.jblf-btn {
  width: 100%;
  padding: clamp(11px,1.5vw,14px);
  background: linear-gradient(90deg, #7B1C1C, #5A1010);
  border: 1.5px solid #C9962A;
  border-radius: 8px;
  color: #FDF6EC;
  font-size: clamp(13.5px,1.5vw,15px);
  font-weight: 700;
  font-family: 'Playfair Display', serif;
  letter-spacing: 0.4px; cursor: pointer;
  transition: opacity 0.18s, transform 0.15s;
}
.jblf-btn:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
.jblf-btn:disabled { opacity: 0.55; cursor: not-allowed; }

/* register link */
.jblf-register {
  text-align: center;
  font-size: clamp(12px,1.2vw,13px);
  color: #8B7355;
}
.jblf-register a { color: #7B1C1C; font-weight: 600; text-decoration: underline; }

/* copyright */
.jblf-copy {
  text-align: center;
  font-size: 10.5px; color: #A89070; margin-top: -6px;
}

/* ════════════════════
   RESPONSIVE
════════════════════ */
@media (max-width: 720px) {
  .jblf-wrap { flex-direction: column; max-width: 480px; }
  .jblf-left {
    flex: none; flex-direction: row; flex-wrap: wrap;
    align-items: center; padding: 18px 20px; gap: 12px;
  }
  .jblf-om { font-size: 26px; }
  .jblf-sub, .jblf-pills, .jblf-divrow { display: none; }
  .jblf-right { padding: 24px 20px 28px; }
}
@media (max-width: 400px) {
  .jblf-left { padding: 14px 16px; }
  .jblf-tagline { font-size: 15px; }
  .jblf-right { padding: 20px 14px 24px; }
}
@media (max-width: 360px) {
  .jblf-page { padding: 0; align-items: flex-start; }
  .jblf-wrap { border-radius: 0; box-shadow: none; }
}
`;

/* ── Lotus SVG ── */
const Lotus = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 28 28"
    fill="none"
    aria-hidden="true"
  >
    <ellipse cx="14" cy="16" rx="4" ry="9" fill="#C9962A" opacity=".9" />
    <ellipse
      cx="14"
      cy="16"
      rx="4"
      ry="8"
      fill="#C9962A"
      opacity=".85"
      transform="rotate(-30 14 16)"
    />
    <ellipse
      cx="14"
      cy="16"
      rx="4"
      ry="8"
      fill="#C9962A"
      opacity=".85"
      transform="rotate(30 14 16)"
    />
    <ellipse
      cx="14"
      cy="16"
      rx="3.5"
      ry="6"
      fill="#C9962A"
      opacity=".6"
      transform="rotate(-58 14 16)"
    />
    <ellipse
      cx="14"
      cy="16"
      rx="3.5"
      ry="6"
      fill="#C9962A"
      opacity=".6"
      transform="rotate(58 14 16)"
    />
    <ellipse cx="14" cy="24" rx="7" ry="2.5" fill="#C9962A" opacity=".25" />
  </svg>
);

/* ── Mandala SVG ── */
const Mandala = () => (
  <svg
    className="jblf-mandala"
    viewBox="0 0 400 400"
    fill="none"
    aria-hidden="true"
  >
    {[30, 65, 100, 135, 165, 190].map((r, i) => (
      <circle
        key={i}
        cx="200"
        cy="200"
        r={r}
        stroke="#C9962A"
        strokeWidth={i === 5 ? 1.5 : 0.8}
        strokeDasharray={i === 1 ? "4 4" : undefined}
      />
    ))}
    {Array.from({ length: 16 }, (_, i) => {
      const a = i * 22.5 * (Math.PI / 180);
      const x = 200 + 95 * Math.cos(a),
        y = 200 + 95 * Math.sin(a);
      const cx1 = 200 + 135 * Math.cos(a + 0.18),
        cy1 = 200 + 135 * Math.sin(a + 0.18);
      const cx2 = 200 + 135 * Math.cos(a - 0.18),
        cy2 = 200 + 135 * Math.sin(a - 0.18);
      return (
        <path
          key={i}
          d={`M200,200 Q${cx1},${cy1} ${x},${y} Q${cx2},${cy2} 200,200`}
          stroke="#C9962A"
          strokeWidth=".7"
        />
      );
    })}
    {Array.from({ length: 12 }, (_, i) => {
      const a = i * 30 * (Math.PI / 180);
      return (
        <line
          key={i}
          x1={200 + 22 * Math.cos(a)}
          y1={200 + 22 * Math.sin(a)}
          x2={200 + 65 * Math.cos(a)}
          y2={200 + 65 * Math.sin(a)}
          stroke="#C9962A"
          strokeWidth=".8"
        />
      );
    })}
    <circle cx="200" cy="200" r="22" stroke="#C9962A" strokeWidth="1" />
  </svg>
);

/* ── Error toast content ── */
const ErrorToast = ({ message }) => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -5 }}
    transition={{ duration: 0.35 }}
    style={{
      background: "#FDF6EC",
      color: "#C0392B",
      border: "1px solid rgba(192,57,43,0.4)",
      padding: "10px 16px",
      borderRadius: 10,
      boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontFamily: "Inter,sans-serif",
      fontSize: 13,
      fontWeight: 500,
    }}
  >
    <FaTimes style={{ flexShrink: 0 }} /> {message}
  </motion.div>
);

/* ─── Component ───────────────────────────────────────────────────────────── */
const LoginForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [cardError, setCardError] = useState("");
  const [focused, setFocused] = useState(null);

  useEffect(() => {
    const id = "jblf-styles";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id;
      el.textContent = CSS;
      document.head.appendChild(el);
    }
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  const handleChange = (e) =>
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setCardError("");
    try {
      const { data } = await axios.post("/api/accounts/login/", formData);
      const { access, refresh, user } = data;
      localStorage.setItem("accessToken", access);
      localStorage.setItem("refreshToken", refresh);
      localStorage.setItem("user", JSON.stringify(user));
      toast.success("🪷 Welcome back!");

      const profileRes = await axios.get("/api/check-profile/", {
        headers: { Authorization: `Bearer ${access}` },
      });
      if (!profileRes.data.is_complete) {
        navigate("/register-step2-html");
        return;
      }

      const memberRes = await axios.get("/api/memberships/my-active/", {
        headers: { Authorization: `Bearer ${access}` },
      });
      navigate(
        memberRes.data.is_active ? "/profile-html-php" : "/membership-pricing",
      );
    } catch (err) {
      const respData = err.response?.data;

      // Unverified account: backend already resent a fresh OTP, so route
      // the user straight to verification instead of showing a dead-end error.
      if (respData?.needs_verification) {
        localStorage.setItem("pendingUserEmail", respData.email);
        toast.custom(
          (t) => (
            <AnimatePresence>
              {t.visible && (
                <ErrorToast message="Your account isn't verified yet. We've sent a new OTP." />
              )}
            </AnimatePresence>
          ),
          { duration: 4000 },
        );
        navigate("/verify-otp");
        return;
      }

      setCardError(
        respData?.error || "Incorrect email or password. Please try again.",
      );
      toast.custom(
        (t) => (
          <AnimatePresence>
            {t.visible && (
              <ErrorToast
                message={respData?.error || "Incorrect email or password"}
              />
            )}
          </AnimatePresence>
        ),
        { duration: 3000 },
      );
    } finally {
      setLoading(false);
    }
  };

  const ic = (name) =>
    ["jblf-input", cardError && name === "email" ? "err" : ""]
      .filter(Boolean)
      .join(" ");

  return (
    <div className="jblf-page">
      <div className="jblf-dust" aria-hidden="true" />
      <Mandala />

      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          style: { fontFamily: "Inter,sans-serif", fontSize: 13 },
        }}
      />

      <motion.div
        className="jblf-wrap"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.42, ease: "easeOut" }}
      >
        {/* ── LEFT ── */}
        <div className="jblf-left">
          <div className="jblf-om" aria-label="Om">
            ꣽ
          </div>
          <h1 className="jblf-tagline">
            Divine Values.
            <br />
            Sacred Bond.
            <br />
            <em>Eternal Together.</em>
          </h1>
          <div className="jblf-divrow" aria-hidden="true">
            <div className="jblf-divline" />
            <span className="jblf-divsym">☸</span>
            <div className="jblf-divline" />
          </div>
          <p className="jblf-sub">
            Sign in to continue your sacred journey — where Ahimsa guides every
            match.
          </p>
          <ul className="jblf-pills" aria-label="Platform highlights">
            {[
              "Verified Jain profiles",
              "Privacy-first matchmaking",
              "Guided by Jain values",
            ].map((t) => (
              <li key={t} className="jblf-pill">
                <span className="jblf-dot" aria-hidden="true" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        {/* ── RIGHT ── */}
        <div className="jblf-right">
          <div className="jblf-form-head">
            <h2 className="jblf-form-title">
              Welcome <em>Back</em>
            </h2>
            <p className="jblf-form-sub">
              Sign in to your Jain Var Vadhu profile
            </p>
          </div>

          {/* lotus divider */}
          <div className="jblf-lrow" aria-hidden="true">
            <div className="jblf-lline" />
            <Lotus />
            <div className="jblf-lline" />
          </div>

          {/* error banner */}
          <AnimatePresence>
            {cardError && (
              <motion.div
                className="jblf-error"
                role="alert"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.28 }}
              >
                <FaTimes aria-hidden="true" /> {cardError}
              </motion.div>
            )}
          </AnimatePresence>

          <form
            onSubmit={handleSubmit}
            noValidate
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "clamp(12px,1.8vw,16px)",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Email */}
              <div className="jblf-field">
                <label className="jblf-label" htmlFor="jb-email">
                  Email Address
                </label>
                <input
                  id="jb-email"
                  className={ic("email")}
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocused("email")}
                  onBlur={() => setFocused(null)}
                  autoComplete="email"
                  inputMode="email"
                  required
                />
              </div>

              {/* Password */}
              <div className="jblf-field">
                <label className="jblf-label" htmlFor="jb-pwd">
                  Password
                </label>
                <input
                  id="jb-pwd"
                  className="jblf-input"
                  type="password"
                  name="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => setFocused("password")}
                  onBlur={() => setFocused(null)}
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>

            {/* Forgot password */}
            <div className="jblf-forgot">
              <Link to="/forgot-password">Forgot Password?</Link>
            </div>

            {/* Submit */}
            <button type="submit" disabled={loading} className="jblf-btn">
              {loading ? "Signing in…" : "🪷 Sign In"}
            </button>
          </form>
          <p className="jblf-register">
            New to Jain Var Vadhu? <Link to="/">Register Free</Link>
          </p>
          <p className="jblf-copy">
            © {new Date().getFullYear()} Jain Var Vadhu. All rights reserved.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginForm;
